import { GeoBounds, LatLngPair } from "./aiMappingGeo";

/**
 * sessionStorage key marking a map as just created via "New Map" — the map
 * page uses it to skip AI controls on the very first visit (AI controls show
 * for existing maps once the organization has the add-on).
 */
export const AI_MAPPING_JUST_CREATED_KEY = "bitumio:ai-mapping-just-created-map-id";

export const aiMappingProposalStorageKey = (projectMapId: string): string =>
  `bitumio:ai-proposal-for-map:${projectMapId}`;

export const aiMappingCostSelectionKey = (areaId: string, category: string): string =>
  `bitumio:ai-cost-selection:${areaId}:${category}`;

export interface AiMappingAreaLike {
  id: string;
  name?: string | null;
  inputType: string; // "BBOX" | "POLYGON"
  coordinates?: string | null;
  status: string;
  taskId?: string | null;
  currentVersion: number;
  imageWidth?: number | null;
  imageHeight?: number | null;
}

export interface AiMappingRunLike {
  id: string;
  name: string;
  description?: string | null;
  areas: AiMappingAreaLike[];
}

export interface AiMappingDetectionLike {
  id: string;
  version: number;
  category: string;
  bbox: number[];
  confidence?: number | null;
  color?: string | null;
  isDeleted: boolean;
}

export interface AiMappingSegmentationLike {
  id: string;
  version: number;
  category: string;
  polygon: number[];
  areaSqft?: number | null;
  color?: string | null;
  isDeleted: boolean;
}

export interface AiMappingResultsLike {
  area: AiMappingAreaLike;
  detections: AiMappingDetectionLike[];
  segmentations: AiMappingSegmentationLike[];
}

export interface AiMappingDetectionGroup<TDetection = AiMappingDetectionLike> {
  category: string;
  color: string;
  count: number;
  items: TDetection[];
}

export interface AiMappingGeoDetection<TDetection = AiMappingDetectionLike> {
  detection: TDetection;
  color: string;
  position: LatLngPair | null;
  areaId?: string;
}

export interface AiMappingGeoSegmentation {
  segmentation: AiMappingSegmentationLike;
  color: string;
  ring: LatLngPair[] | null;
  areaId?: string;
}

export interface AiMappingGeoContext {
  bounds: GeoBounds | null;
  imageWidth: number | null;
  imageHeight: number | null;
}

export interface AiMappingCostSelection {
  productId?: string | null;
  proposalProductId?: string | null;
  additionalCostId?: string | null;
}

export interface AiMappingMapDriver {
  startDraw(
    inputType: "BBOX" | "POLYGON" | "LINE",
    onDrawn: (points: LatLngPair[]) => void,
    onCancel?: () => void,
    options?: { showDraft?: boolean }
  ): void;
  cancelDraw(): void;
  clearDraft(): void;
  setClickHandler(handler: ((latLng: LatLngPair) => void) | null): void;
  syncAreas(
    areas: AiMappingAreaLike[],
    selectedAreaId: string | null,
    onSelectArea?: (areaId: string) => void,
    onClearArea?: () => void
  ): void;
  syncDetections(
    detections: AiMappingGeoDetection[],
    selectedDetectionId?: string | null,
    onSelect?: (detectionId: string, areaId?: string) => void
  ): void;
  syncSegmentations(
    segmentations: AiMappingGeoSegmentation[],
    visible: boolean,
    selectedSegmentationId?: string | null,
    onSelectSegmentation?: (segmentationId: string, areaId?: string) => void
  ): void;
  fitToPoints(points: LatLngPair[]): void;
  clearAll(): void;
}
